import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from "./authService";

// API functions
export const serviceCategoryApi = {
  // Get all service categories
  getAll: async (params = {}) => {
    const response = await api.get('/service-categories', { params });
    return response.data;
  },

  // Get service category by ID
  getById: async (id) => {
    const response = await api.get(`/service-categories/${id}`);
    return response.data;
  },

  // Get featured service categories
  getFeatured: async () => {
    const response = await api.get('/service-categories/featured');
    return response.data;
  },

  // Get categories by type
  getByType: async (type) => {
    const response = await api.get(`/service-categories/type/${type}`);
    return response.data;
  },

  // Admin: Create service category
  create: async (data) => {
    const response = await api.post('/admin/service-categories', data);
    return response.data;
  },

  // Admin: Update service category
  update: async ({ id, data }) => {
    const response = await api.put(`/admin/service-categories/${id}`, data);
    return response.data;
  },

  // Admin: Delete service category
  delete: async (id) => {
    const response = await api.delete(`/admin/service-categories/${id}`);
    return response.data;
  },

  // Admin: Toggle category status
  toggleStatus: async (id) => {
    const response = await api.put(`/admin/service-categories/${id}/toggle-status`);
    return response.data;
  },

  // Admin: Bulk update sort order
  updateSortOrder: async (categories) => {
    const response = await api.put('/admin/service-categories/sort-order', { categories });
    return response.data;
  }
};

// React Query hooks for public endpoints
export const useServiceCategories = (params = {}) => {
  return useQuery({
    queryKey: ['service-categories', params],
    queryFn: () => serviceCategoryApi.getAll(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
  });
};

export const useServiceCategory = (id) => {
  return useQuery({
    queryKey: ['service-category', id],
    queryFn: () => serviceCategoryApi.getById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useFeaturedCategories = () => {
  return useQuery({
    queryKey: 'featured-categories',
    queryFn: serviceCategoryApi.getFeatured,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes (renamed from cacheTime in v5)
  });
};

export const useCategoriesByType = (type) => {
  return useQuery({
    queryKey: ['categories-by-type', type],
    queryFn: () => serviceCategoryApi.getByType(type),
    enabled: !!type,
    staleTime: 5 * 60 * 1000,
  });
};

// React Query hooks for admin endpoints
export const useAdminServiceCategories = (params = {}) => {
  return useQuery({
    queryKey: ['admin-service-categories', params],
    queryFn: () => serviceCategoryApi.getAll(params),
    staleTime: 2 * 60 * 1000, // 2 minutes for admin data
    gcTime: 5 * 60 * 1000, // 5 minutes (renamed from cacheTime in v5)
  });
};

export const useCreateServiceCategory = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: serviceCategoryApi.create,
    onSuccess: (data) => {
      // Invalidate and refetch service categories
      queryClient.invalidateQueries({ queryKey: ['admin-service-categories'] });
      queryClient.invalidateQueries({ queryKey: ['service-categories'] });
      queryClient.invalidateQueries({ queryKey: ['featured-categories'] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useUpdateServiceCategory = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: serviceCategoryApi.update,
    onSuccess: (data, variables) => {
      // Invalidate and refetch service categories
      queryClient.invalidateQueries({ queryKey: ['admin-service-categories'] });
      queryClient.invalidateQueries({ queryKey: ['service-categories'] });
      queryClient.invalidateQueries({ queryKey: ['service-category', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['featured-categories'] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useDeleteServiceCategory = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: serviceCategoryApi.delete,
    onSuccess: (data, categoryId) => {
      // Invalidate and refetch service categories
      queryClient.invalidateQueries({ queryKey: ['admin-service-categories'] });
      queryClient.invalidateQueries({ queryKey: ['service-categories'] });
      queryClient.invalidateQueries({ queryKey: ['service-category', categoryId] });
      queryClient.invalidateQueries({ queryKey: ['featured-categories'] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useToggleCategoryStatus = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: serviceCategoryApi.toggleStatus,
    onSuccess: (data, categoryId) => {
      // Invalidate and refetch service categories
      queryClient.invalidateQueries({ queryKey: ['admin-service-categories'] });
      queryClient.invalidateQueries({ queryKey: ['service-categories'] });
      queryClient.invalidateQueries({ queryKey: ['service-category', categoryId] });
      queryClient.invalidateQueries({ queryKey: ['featured-categories'] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

export const useUpdateSortOrder = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: serviceCategoryApi.updateSortOrder,
    onSuccess: (data) => {
      // Invalidate and refetch service categories
      queryClient.invalidateQueries({ queryKey: ['admin-service-categories'] });
      queryClient.invalidateQueries({ queryKey: ['service-categories'] });
      queryClient.invalidateQueries({ queryKey: ['featured-categories'] });
      
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    }
  });
};

// Service type constants
export const SERVICE_TYPES = [
  { value: 'photo_video', label: 'Photo & Video' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'event_services', label: 'Event Services' },
  { value: 'venue_services', label: 'Venue Services' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'beauty_wellness', label: 'Beauty & Wellness' },
  { value: 'technical_services', label: 'Technical Services' },
  { value: 'specialty_services', label: 'Specialty Services' },
  { value: 'corporate_services', label: 'Corporate Services' },
  { value: 'wedding_services', label: 'Wedding Services' }
];

export default serviceCategoryApi;
